'use client';

import { Checkbox } from '@ui/components/checkbox';
import { Input } from '@ui/components/input';
import { AppCard } from '@ui/components/layouts';
import { SimpleSelect } from '@ui/components/select';
import { cn } from '@ui/lib/utils';
import { text } from '@modules/garden-config/text';
import { text as gardenConfigText } from '@modules/garden-config/text_business_logic';
import { parseSowSelectValue } from '@modules/garden-config/business_logic';
import { PlantAvatarWeb } from '@modules/plants/views/plant_avatar_web';

import type {
  Plant,
  SowingConfigField,
  SowingConfigFieldValue,
  SowingConfigValues,
} from '@modules/garden-config/models';

import { BulkSowingActions } from './bulk_sowing_actions_view';

const sowWeekOptions = gardenConfigText.sowWeekSelectOptions();
const sowMonthOptions = gardenConfigText.sowMonthSelectOptions();

export interface SowingConfigViewProps {
  plantConfigs: Array<{ plant: Plant; config: SowingConfigValues }>;
  selectedPlantIds: Set<string>;
  bulkSeedBuffer: number;
  bulkIsActive: boolean;
  onUpdateConfig: (
    plantId: string,
    field: SowingConfigField,
    value: SowingConfigFieldValue
  ) => void;
  onToggleSelection: (plantId: string) => void;
  onSelectAll: (plantIds: string[]) => void;
  onBulkApply: (seedBuffer: number, isActive: boolean) => void;
  onClearSelection: () => void;
  onBulkSeedBufferChange: (buffer: number) => void;
  onBulkIsActiveChange: (active: boolean) => void;
}

export function SowingConfig({
  plantConfigs,
  selectedPlantIds,
  bulkSeedBuffer,
  bulkIsActive,
  onUpdateConfig,
  onToggleSelection,
  onSelectAll,
  onBulkApply,
  onClearSelection,
  onBulkSeedBufferChange,
  onBulkIsActiveChange,
}: SowingConfigViewProps) {
  if (plantConfigs.length === 0) {
    return (
      <div className="flex flex-col items-center py-10">
        <p className="text-muted-foreground text-lg font-medium">{text.noPlants}</p>
      </div>
    );
  }

  const allSelected =
    plantConfigs.length > 0 && plantConfigs.every((p) => selectedPlantIds.has(p.plant.id));

  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted-foreground text-sm">{text.sowingDescription}</p>

      {selectedPlantIds.size > 0 && (
        <BulkSowingActions
          selectedCount={selectedPlantIds.size}
          bulkSeedBuffer={bulkSeedBuffer}
          bulkIsActive={bulkIsActive}
          onBulkSeedBufferChange={onBulkSeedBufferChange}
          onBulkIsActiveChange={onBulkIsActiveChange}
          onApply={onBulkApply}
          onClear={onClearSelection}
        />
      )}

      <AppCard className="overflow-hidden p-0">
        <div className="border-border bg-muted/40 flex items-center border-b p-3">
          <div className="flex w-12 items-center justify-center">
            <Checkbox
              checked={allSelected}
              onCheckedChange={(checked) => {
                if (checked === true) {
                  onSelectAll(plantConfigs.map((p) => p.plant.id));
                } else {
                  onSelectAll([]);
                }
              }}
            />
          </div>
          <span className="flex-1 font-semibold">{text.plant}</span>
          <span className="w-[140px] font-semibold">{text.sowMonth}</span>
          <span className="w-[120px] font-semibold">{text.sowWeek}</span>
          <span className="w-32 font-semibold">{text.seedBuffer}</span>
          <span className="w-24 font-semibold">{text.active}</span>
        </div>

        {plantConfigs.map(({ plant, config }) => {
          const isSelected = selectedPlantIds.has(plant.id);

          return (
            <div
              key={plant.id}
              className={cn(
                'border-border flex items-center border-b p-3 last:border-b-0',
                isSelected ? 'bg-green-50 hover:bg-green-100' : 'bg-background hover:bg-muted/40'
              )}>
              <div className="flex w-12 items-center justify-center">
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => onToggleSelection(plant.id)}
                />
              </div>
              <div className="flex flex-1 items-center gap-2">
                <PlantAvatarWeb
                  name={plant.name}
                  emoji={plant.emoji}
                  imagePath={plant.imagePath}
                  size={28}
                />
                <span className="font-medium">{plant.name}</span>
              </div>
              <div className="w-[140px]">
                <SimpleSelect
                  id={`sow-month-${plant.id}`}
                  className="w-[120px]"
                  value={config.sowMonth !== undefined ? String(config.sowMonth) : ''}
                  onValueChange={(value) =>
                    onUpdateConfig(plant.id, 'sowMonth', parseSowSelectValue(value))
                  }
                  options={sowMonthOptions}
                  placeholder={text.sowMonthPlaceholder}
                />
              </div>
              <div className="w-[120px]">
                <SimpleSelect
                  id={`sow-week-${plant.id}`}
                  className="w-[100px]"
                  value={config.sowWeekOfMonth !== undefined ? String(config.sowWeekOfMonth) : ''}
                  onValueChange={(value) =>
                    onUpdateConfig(plant.id, 'sowWeekOfMonth', parseSowSelectValue(value))
                  }
                  options={sowWeekOptions}
                  placeholder={text.sowWeekPlaceholder}
                />
              </div>
              <div className="w-32">
                <Input
                  className="w-20"
                  inputMode="numeric"
                  value={String(config.seedBuffer)}
                  onChange={(event) => {
                    const parsed = parseInt(event.target.value, 10);
                    if (!isNaN(parsed) && parsed >= 0) {
                      onUpdateConfig(plant.id, 'seedBuffer', parsed);
                    }
                  }}
                />
              </div>
              <div className="flex w-24 items-center">
                <Checkbox
                  checked={config.isActive}
                  onCheckedChange={(checked) => {
                    onUpdateConfig(plant.id, 'isActive', checked === true);
                  }}
                />
              </div>
            </div>
          );
        })}
      </AppCard>
    </div>
  );
}
